import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown, Loader2 } from "lucide-react";
import { SourceCombobox } from "@/components/review/SourceCombobox";
import { UserCombobox } from "@/components/capabilities/UserCombobox";
import { JsonBlock } from "@/components/ui/json-block";
import { useScrapersSources } from "@/hooks/useScrapersSources";
import {
  useCapabilityExplain,
  useCapabilityRegistry,
} from "@/hooks/useCapabilities";

export function CapabilityExplainPanel({
  initialCapability,
  initialSourceId,
  initialUserId,
}: {
  initialCapability?: string | undefined;
  initialSourceId?: string | undefined;
  initialUserId?: string | undefined;
}) {
  const [capability, setCapability] = useState(initialCapability ?? "");
  const [sourceId, setSourceId] = useState<string | undefined>(initialSourceId);
  const [userId, setUserId] = useState<string | undefined>(initialUserId);
  const [rawOpen, setRawOpen] = useState(false);

  const { data: registry = [] } = useCapabilityRegistry();
  const { data: sources = [] } = useScrapersSources();
  const selectedSpec = registry.find((e) => e.capability === capability);

  const explainQuery = useCapabilityExplain(
    capability || undefined,
    {
      userId: userId ? Number(userId) : undefined,
      sourceId: selectedSpec?.source_scopable ? sourceId : undefined,
    },
    { enabled: Boolean(capability) },
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Explain</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="explain-capability">Capability</Label>
            <Select value={capability} onValueChange={setCapability}>
              <SelectTrigger id="explain-capability" className="mt-1">
                <SelectValue placeholder="Select capability..." />
              </SelectTrigger>
              <SelectContent>
                {registry.map((entry) => (
                  <SelectItem key={entry.capability} value={entry.capability}>
                    {entry.capability}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>User (optional)</Label>
            <div className="mt-1">
              <UserCombobox
                value={userId}
                onValueChange={setUserId}
                className="w-full"
              />
            </div>
          </div>
          <div>
            <Label>Source (optional)</Label>
            <div className="mt-1">
              <SourceCombobox
                sources={sources}
                value={sourceId}
                onValueChange={setSourceId}
                className="w-full"
              />
            </div>
            {capability && !selectedSpec?.source_scopable && (
              <p className="mt-1 text-xs text-muted-foreground">
                {capability} is not source-scopable; source is ignored.
              </p>
            )}
          </div>
        </div>

        {explainQuery.isPending && capability ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Resolving…
          </div>
        ) : explainQuery.isError ? (
          <p className="text-sm text-destructive">
            {explainQuery.error instanceof Error
              ? explainQuery.error.message
              : "Failed to resolve."}
          </p>
        ) : explainQuery.data ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant={explainQuery.data.allowed ? "default" : "destructive"}
                className="text-base px-3 py-1"
              >
                {explainQuery.data.allowed ? "Allowed" : "Denied"}
              </Badge>
              <Badge variant="outline" className="font-mono text-xs">
                {explainQuery.data.layer}
              </Badge>
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-muted-foreground">Kind</dt>
                <dd>{explainQuery.data.kind}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Admin bypass</dt>
                <dd>{explainQuery.data.admin_bypass ? "Yes" : "No"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Required tier</dt>
                <dd>{explainQuery.data.required_tier ?? "—"}</dd>
              </div>
            </dl>

            {explainQuery.data.requires && (
              <Card className="border-dashed">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Requires: {explainQuery.data.requires.capability}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap items-center gap-2 pt-0">
                  <Badge
                    variant={
                      explainQuery.data.requires.allowed
                        ? "default"
                        : "destructive"
                    }
                  >
                    {explainQuery.data.requires.allowed ? "Allowed" : "Denied"}
                  </Badge>
                  <Badge variant="outline" className="font-mono text-xs">
                    {explainQuery.data.requires.layer}
                  </Badge>
                </CardContent>
              </Card>
            )}

            <Collapsible open={rawOpen} onOpenChange={setRawOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1">
                  <ChevronsUpDown className="h-3.5 w-3.5" />
                  {rawOpen ? "Hide" : "Show"} raw payload
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <JsonBlock
                  value={explainQuery.data}
                  className="mt-2 rounded-md border bg-muted/50 p-3 text-xs"
                />
              </CollapsibleContent>
            </Collapsible>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Select a capability to resolve its effective decision.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
